import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, XCircle, TrendingUp, Search } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ENV } from '@/config/env';

type QuoteStatus = 'sent' | 'accepted' | 'closed';

export function ProviderQuotesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<QuoteStatus>('sent');
  const [searchTerm, setSearchTerm] = useState('');
  const [quotes, setQuotes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quotes/sent`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredQuotes = () => {
    let filtered = quotes;

    // Filter by status
    if (activeTab === 'sent') {
      // Sent = pending quotes
      filtered = filtered.filter(q => q.status?.toLowerCase() === 'pending' || q.status?.toLowerCase() === 'sent');
    } else if (activeTab === 'accepted') {
      // Accepted quotes
      filtered = filtered.filter(q => q.status?.toLowerCase() === 'accepted');
    } else if (activeTab === 'closed') {
      // Closed = completed or rejected
      filtered = filtered.filter(q => 
        q.status?.toLowerCase() === 'rejected' || 
        q.status?.toLowerCase() === 'completed' ||
        q.status?.toLowerCase() === 'withdrawn'
      );
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(q =>
        q.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredQuotes = getFilteredQuotes();

  const getStatusCounts = () => ({
    sent: quotes.filter(q => q.status?.toLowerCase() === 'pending' || q.status?.toLowerCase() === 'sent').length,
    accepted: quotes.filter(q => q.status?.toLowerCase() === 'accepted').length,
    closed: quotes.filter(q => 
      q.status?.toLowerCase() === 'rejected' || 
      q.status?.toLowerCase() === 'completed' ||
      q.status?.toLowerCase() === 'withdrawn'
    ).length
  });

  const statusCounts = getStatusCounts();

  const tabs = [
    { id: 'sent' as QuoteStatus, label: 'Sent', count: statusCounts.sent, icon: Clock, description: 'Quotes awaiting customer response' },
    { id: 'accepted' as QuoteStatus, label: 'Accepted', count: statusCounts.accepted, icon: CheckCircle, description: 'Quotes accepted by customers' },
    { id: 'closed' as QuoteStatus, label: 'Closed', count: statusCounts.closed, icon: XCircle, description: 'Completed or rejected quotes' }
  ];

  const getStatusBadge = (status: string) => {
    const s = status?.toLowerCase();
    if (s === 'pending' || s === 'sent') return <Badge variant="warning" size="sm"><Clock className="w-3 h-3 mr-1" />Sent</Badge>;
    if (s === 'accepted') return <Badge variant="success" size="sm"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>;
    if (s === 'rejected') return <Badge variant="error" size="sm"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    if (s === 'completed') return <Badge variant="secondary" size="sm"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
    if (s === 'withdrawn') return <Badge variant="secondary" size="sm"><XCircle className="w-3 h-3 mr-1" />Withdrawn</Badge>;
    return <Badge variant="secondary" size="sm">{status}</Badge>;
  };

  const calculateWinRate = () => {
    const total = quotes.length;
    if (total === 0) return 0;
    const accepted = quotes.filter(q => q.status?.toLowerCase() === 'accepted').length;
    return Math.round((accepted / total) * 100);
  };

  const winRate = calculateWinRate();

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={[
          { label: 'Dashboard', href: '/provider/dashboard' },
          { label: 'Quotes' }
        ]} />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Quotes</h1>
            <p className="text-gray-600 mt-1">Track and manage your quote submissions</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900">{statusCounts.sent}</div>
                <div className="text-sm text-gray-600">Sent (Awaiting Response)</div>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900">{statusCounts.accepted}</div>
                <div className="text-sm text-gray-600">Accepted</div>
              </div>
              <CheckCircle className="w-8 h-8 text-success-600" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900">{winRate}%</div>
                <div className="text-sm text-gray-600">Win Rate</div>
              </div>
              <TrendingUp className="w-8 h-8 text-primary-600" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-x-auto">
          <div className="flex">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600 bg-primary-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <Badge variant="secondary" size="sm">
                    {tab.count}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </Card>

        {/* Quotes List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No quotes found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Start sending quotes to customers'}
            </p>
            <Button onClick={() => navigate('/provider-jobs')}>
              Browse Available Jobs
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuotes.map((quote) => (
              <Card
                key={quote.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/jobs/${quote.job_id || quote.id}`)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {quote.title}
                        </h3>
                        {getStatusBadge(quote.status)}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {quote.description}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-gray-900">
                        €{quote.amount?.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  {quote.estimated_duration && (
                    <div className="text-sm text-gray-600">
                      ⏱️ {quote.estimated_duration}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
